import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, CheckCheck } from "lucide-react";

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "adesso";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  return new Date(iso).toLocaleDateString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const loadCount = useCallback(async () => {
    try { const { data } = await api.get("/notifiche/count"); setCount(data.non_lette); } catch (e) { console.error("Errore conteggio notifiche:", e); }
  }, []);

  const loadItems = useCallback(async () => {
    try { const { data } = await api.get("/notifiche"); setItems(data); } catch (e) { console.error("Errore caricamento notifiche:", e); }
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 15000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => { if (open) loadItems(); }, [open, loadItems]);

  const openItem = async (n) => {
    if (!n.letta) {
      try { await api.post(`/notifiche/${n.id}/letta`); } catch (e) { console.error("Errore segna notifica letta:", e); }
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, letta: true } : x)));
      setCount((c) => Math.max(0, c - 1));
    }
    setOpen(false);
    if (n.url && n.url.startsWith("/")) nav(n.url);
  };

  const leggiTutte = async () => {
    try { await api.post("/notifiche/leggi-tutte"); } catch (e) { console.error("Errore segna tutte lette:", e); }
    setItems((prev) => prev.map((x) => ({ ...x, letta: true })));
    setCount(0);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-[#0F2B48] hover:bg-[#F7941E]/10" data-testid="notification-bell">
          <Bell className="w-5 h-5" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="notification-badge">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0]">
          <div className="font-semibold text-[#0F2B48]">Notifiche</div>
          {items.some((x) => !x.letta) && (
            <button onClick={leggiTutte} className="text-xs text-[#F7941E] hover:underline flex items-center gap-1" data-testid="notification-read-all">
              <CheckCheck className="w-3.5 h-3.5" /> Segna tutte lette
            </button>
          )}
        </div>
        <ScrollArea className="max-h-96">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-stone-500" data-testid="notification-empty">Nessuna notifica</div>
          ) : (
            items.map((n) => (
              <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-item-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-[#F1F5F9] hover:bg-[#F8FAFC] transition-colors ${n.letta ? "" : "bg-[#FFF9F0]"}`}>
                <div className="flex items-start gap-2">
                  {!n.letta && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#F7941E] shrink-0" />}
                  <div className={n.letta ? "pl-4" : ""}>
                    <div className="text-sm font-semibold text-[#0F2B48]">{n.title}</div>
                    <div className="text-sm text-stone-600 leading-snug">{n.body}</div>
                    <div className="text-[11px] text-stone-400 mt-1">{timeAgo(n.created_at)}</div>
                  </div>
                </div>
              </button>
            ))
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
